import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { Observable } from 'rxjs';
import { ConfirmationService } from 'primeng/api';

import { FormEditor } from '@composer/components/form-editor/form-editor';

/**
 * CanDeactivate guard for the composer's `form/:formId` route (FormEditor). Blocks
 * in-app navigation away from an unsaved WYSIWYG designer arrangement, asking for
 * confirmation via PrimeNG's ConfirmationService -- the route provides
 * `ConfirmationService` (see app.routes.ts) so this guard and FormEditor's own
 * `<p-confirmdialog>` share the same instance.
 *
 * Complements (does not replace) `FormEditor.onBeforeUnload()`, which covers an
 * actual tab close/refresh -- a case this guard, being Angular-router-only, can't see.
 */
export const composerUnsavedChangesGuard: CanDeactivateFn<FormEditor> = component => {
  if (!component.layoutDirty()) {
    return true;
  }

  const confirmationService = inject(ConfirmationService);

  return new Observable<boolean>(subscriber => {
    confirmationService.confirm({
      header: 'Cambios sin guardar',
      message: 'Tienes cambios de diseño sin guardar. ¿Salir y descartarlos?',
      icon: 'pi pi-exclamation-triangle',
      // F4: `closable: false` removes PrimeNG's default `<p-dialog>` close (X) button,
      // which otherwise renders with no accessible name (AXE `button-name`, critical) --
      // "Seguir editando" already covers "close without leaving". The reject button's
      // `severity: 'secondary'` fixes its low default contrast against the dialog
      // background. See ui-v3/primeng.txt / node_modules/primeng/types/primeng-api.d.ts
      // (`Confirmation`) for these properties.
      closable: false,
      acceptButtonProps: { label: 'Salir', severity: 'danger', variant: 'outlined' },
      rejectButtonProps: { label: 'Seguir editando', severity: 'secondary', variant: 'outlined' },
      accept: () => {
        subscriber.next(true);
        subscriber.complete();
      },
      reject: () => {
        subscriber.next(false);
        subscriber.complete();
      },
    });
  });
};
